import { OrganismeInscriptionDto } from '@common/services';
import { SavedFile } from '@common/ui';
import { revokeObjectUrl } from '@common/utils/file';
import { createSlice, PayloadAction } from '@reduxjs/toolkit';

const initialState: InscriptionState = {
  organisme: null,

  admin1: null,
  areAdmin1AndRepresentantSame: false,
  admin2: null,

  representant: null,
  attestation: null,
  delegataire: null,
  delegation: null,

  ndas: [],
  requested: false,
};

const inscriptionSlice = createSlice({
  name: 'inscription',
  initialState,
  reducers: {
    setOrganismeInfo: (state: InscriptionState, action: PayloadAction<OrganismeInscriptionDto>) => {
      state.organisme = action.payload;
      state.requested = true;
    },
    patchOrganismeInfo: (state: InscriptionState, action: PayloadAction<Partial<OrganismeInscriptionDto>>) => {
      state.organisme ??= {};
      state.organisme = { ...state.organisme, ...action.payload };
    },

    setUtilisateurAdmin1: (state: InscriptionState, action: PayloadAction<InscriptionUtilisateur>) => {
      state.admin1 = action.payload;
    },
    setUtilisateurAdmin2: (state: InscriptionState, action: PayloadAction<InscriptionUtilisateur>) => {
      state.admin2 = action.payload;
    },
    resetUtilisateurAdmin2: (state: InscriptionState) => {
      state.admin2 = null;
    },

    setAdmin1AndRepresentantAreSame: (state: InscriptionState, action: PayloadAction<boolean>) => {
      state.areAdmin1AndRepresentantSame = action.payload;
    },

    setUtilisateurRepresentant: (state: InscriptionState, action: PayloadAction<InscriptionUtilisateur>) => {
      state.representant = action.payload;
    },
    setUtilisateurDelegataire: (state: InscriptionState, action: PayloadAction<Omit<InscriptionUtilisateur, 'telephone'>>) => {
      state.delegataire = action.payload;
    },
    resetUtilisateurDelegataire: (state: InscriptionState) => {
      state.delegataire = null;
      revokeObjectUrl(state.delegation?.url);
      state.delegation = null;
    },

    setFichierAttestation: (state: InscriptionState, action: PayloadAction<SavedFile | null>) => {
      revokeObjectUrl(state.attestation?.url);
      state.attestation = action.payload;
    },
    setFichierDelegation: (state: InscriptionState, action: PayloadAction<SavedFile | null>) => {
      revokeObjectUrl(state.delegation?.url);
      state.delegation = action.payload;
    },

    setNdas: (state: InscriptionState, action: PayloadAction<string[]>) => {
      state.ndas = action.payload;
    },

    resetInscription: (state: InscriptionState) => {
      state.organisme = initialState.organisme;

      state.admin1 = initialState.admin1;
      state.admin2 = initialState.admin2;
      state.areAdmin1AndRepresentantSame = initialState.areAdmin1AndRepresentantSame;

      state.representant = initialState.representant;
      revokeObjectUrl(state.attestation?.url);
      state.attestation = initialState.attestation;

      state.delegataire = initialState.delegataire;
      revokeObjectUrl(state.delegation?.url);
      state.delegation = initialState.delegation;

      state.ndas = initialState.ndas;
      state.requested = initialState.requested;
    },
    setRequested: (state: InscriptionState, action: PayloadAction<boolean>) => {
      state.requested = action.payload;
    },
  },
});

export const {
  setOrganismeInfo,
  patchOrganismeInfo,

  setUtilisateurAdmin1,
  setUtilisateurAdmin2,
  resetUtilisateurAdmin2,
  setAdmin1AndRepresentantAreSame,

  setUtilisateurRepresentant,
  setUtilisateurDelegataire,
  resetUtilisateurDelegataire,
  setFichierAttestation,
  setFichierDelegation,

  setNdas,

  resetInscription,
  setRequested,
} = inscriptionSlice.actions;

export default inscriptionSlice.reducer;

export interface InscriptionState {
  organisme: Partial<OrganismeInscriptionDto> | null;

  admin1: InscriptionUtilisateur | null;
  areAdmin1AndRepresentantSame: boolean;
  admin2: InscriptionUtilisateur | null;

  representant: InscriptionUtilisateur | null;
  attestation: SavedFile | null;
  delegataire: Omit<InscriptionUtilisateur, 'telephone'> | null;
  delegation: SavedFile | null;

  ndas: string[];
  requested: boolean;
}

export interface InscriptionUtilisateur {
  nom: string,
  prenom: string,
  email: string,
  travail: string,
  telephone: string,
}